import {
  ArrayControlProps,
  isControl,
  rankWith,
  resolveSchema,
} from "@jsonforms/core";
import { withJsonFormsArrayControlProps } from "@jsonforms/react";
import { ColorBadge } from "@legacy-components/ColorBadge/ColorBadge";
import { useMemo, useState } from "react";
import { Icon, Timeline } from "rsuite";
import "./TimelineRenderer.scss";

const PAGE_SIZE = 5;

const TimelineRenderer = (props: ArrayControlProps) => {
  const data = useMemo(() => (Array.isArray(props.data) ? props.data : []), [
    props.data,
  ]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const properties = props.schema?.properties ?? {};

  const formatDate = (fieldKey: string, date: Date | string) => {
    const dt: Date = date instanceof Date ? date : new Date(date);
    const adProps = properties[fieldKey]?.additionalProperties ?? {};
    const disableTimezoneConversion = (adProps as Record<string, unknown>)
      .disableTimezoneConversion;
    if (disableTimezoneConversion) {
      return new Date(dt.toISOString().replace("Z", "")).toLocaleString();
    }
    return dt.toLocaleString();
  };

  if (data.length === 0) {
    return (
      <div className="group-container">
        <div className="no-data">Nothing to display</div>
      </div>
    );
  }

  return (
    <div className="group-container">
      <Timeline className="TimelineRenderer" align="left">
        {data.slice(0, visibleCount).map((item, index) => (
          <Timeline.Item key={index} dot={<Icon icon="clock-o" />}>
            <div className="timeline-header">
              <span className="timeline-date">
                {item.dtLastModified
                  ? formatDate("dtLastModified", item.dtLastModified)
                  : ""}
              </span>
              {item.dsStatus && (
                <ColorBadge
                  width="120px"
                  height="24px"
                  fontSize="11px"
                  fontWeight="600"
                  semanticValue={item.cdSemanticValue}
                  text={item.dsStatus}
                />
              )}
            </div>
            <div className="timeline-details">
              {item.changeAuthor && (
                <div className="timeline-field">
                  <span className="field-label">
                    {properties.changeAuthor?.title ?? "Change Author"}
                  </span>
                  <span className="field-value">{item.changeAuthor}</span>
                </div>
              )}
              {item.reassignedTo && (
                <div className="timeline-field">
                  <span className="field-label">
                    {properties.reassignedTo?.title ?? "Reassigned To"}
                  </span>
                  <span className="field-value">{item.reassignedTo}</span>
                </div>
              )}
              {item.dsPostponeReason && (
                <div className="timeline-field">
                  <span className="field-label">
                    {properties.dsPostponeReason?.title ?? "Postpone Reason"}
                  </span>
                  <span className="field-value">{item.dsPostponeReason}</span>
                </div>
              )}
            </div>
          </Timeline.Item>
        ))}
      </Timeline>
      {visibleCount < data.length && (
        <button
          className="timeline-load-more"
          onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
        >
          Load More
        </button>
      )}
    </div>
  );
};

export const timelineRendererTester = rankWith(
  100,
  (uischema, schema, context) => {
    if (!isControl(uischema)) return false;
    if (uischema.options?.variant !== "timeline") return false;
    const resolvedSchema = resolveSchema(
      schema,
      uischema.scope,
      context?.rootSchema,
    );
    return resolvedSchema?.type === "array";
  },
);

export default withJsonFormsArrayControlProps(TimelineRenderer);
